import {
  Create,
  Box,
  TextField,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormHelperText,
} from "@pankod/refine-mui";
import { Controller, useForm } from "@pankod/refine-react-hook-form";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = Yup.object({
  name: Yup.string().label("Name").trim().required(),
  symbol: Yup.string().label("Symbol").trim().required(),
  symbol_display: Yup.string().label("Symbol Display").trim().required(),
  min_usd_start: Yup.number().label("Min Usd Start").required(),
  min_usd_stop: Yup.number().label("Min Usd Stop").required(),
  tag: Yup.array()
    .of(Yup.string())
    .label("Tag")
    .transform((value) => value.split(",")),
  about: Yup.string().label("About"),
  bot_file_name: Yup.string().label("Bot File Name").required(),
  timeframe: Yup.string().label("Time frame").required(),
  leverage: Yup.number().label("Leverage").required(),
  percent_win_rate: Yup.number()
    .label("Percent Win Rate")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  total_percent_return: Yup.number()
    .label("Total Percent Winrate")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  percent_max_draw_down: Yup.number()
    .label("Percent max Draw Down")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  recovery_period: Yup.number()
    .integer()
    .label("Recovery Period")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null)),
  trade_count: Yup.number()
    .label("Trade Count")
    .nullable()
    .transform((val) => (val === Number(val) ? val : null))
    .integer(),
  trend_type: Yup.string().label("Trend Type"),
  risk_type: Yup.string().label("Risk Type"),
  backtest_start_at: Yup.date()
    .label("Backtest Start At")
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr)),
  backtest_end_at: Yup.date()
    .label("Backtest End At")
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr)),
});

interface formDataType {
  name: string;
  symbol: string;
  symbol_display: string;
  min_usd_start: number;
  min_usd_stop: number;
  tag: string;
  about: string;
  bot_file_name: string;
  timeframe: string;
  leverage: number;
  percent_win_rate: number;
  total_percent_return: number;
  percent_max_draw_down: number;
  recovery_period: number;
  trade_count: number;
  trend_type: string;
  risk_type: string;
  backtest_start_at: string;
  backtest_end_at: string;
}

export const BotCreate = () => {
  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    formState: { errors },
  } = useForm<formDataType>({ resolver: yupResolver(schema) });

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("name", {
            required: "This field is required",
          })}
          required
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <TextField
          {...register("symbol", {
            required: "This field is required",
          })}
          placeholder="BTC/USDT"
          required
          error={!!(errors as any)?.symbol}
          helperText={(errors as any)?.symbol?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Symbol"
          name="symbol"
        />
        <TextField
          {...register("symbol_display", {
            required: "This field is required",
          })}
          required
          error={!!(errors as any)?.symbol_display}
          helperText={(errors as any)?.symbol_display?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Symbol Display"
          name="symbol_display"
        />
        <TextField
          {...register("min_usd_start", {
            required: "This field is required",
          })}
          required
          error={!!(errors as any)?.min_usd_start}
          helperText={(errors as any)?.min_usd_start?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Minimum Investment Start (USD)"
          name="min_usd_start"
        />
        <TextField
          {...register("min_usd_stop", {
            required: "This field is required",
          })}
          required
          error={!!(errors as any)?.min_usd_stop}
          helperText={(errors as any)?.min_usd_stop?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Minimum Investment Stop (USD)"
          name="min_usd_stop"
        />
        <TextField
          {...register("about")}
          error={!!(errors as any)?.about}
          helperText={(errors as any)?.about?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="About"
          name="about"
        />
        <TextField
          {...register("bot_file_name", {
            required: "This field is required",
          })}
          required
          error={!!(errors as any)?.min_usd_stop}
          helperText={(errors as any)?.min_usd_stop?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Bot Python File Name (without .py)"
          placeholder="rsn_max"
          name="bot_file_name"
        />
        <FormControl>
          <FormLabel required>Time frame</FormLabel>
          <Select
            {...register("timeframe", {
              required: "time frame is require",
            })}
            size="small"
            margin="none"
            variant="outlined"
          >
            <MenuItem value="1m">1m</MenuItem>
            <MenuItem value="5m">5m</MenuItem>
            <MenuItem value="15m">15m</MenuItem>
            <MenuItem value="30m">30m</MenuItem>
            <MenuItem value="1h">1h</MenuItem>
            <MenuItem value="2h">2h</MenuItem>
            <MenuItem value="4h">4h</MenuItem>
            <MenuItem value="6h">6h</MenuItem>
            <MenuItem value="12h">12h</MenuItem>
            <MenuItem value="1d">1d</MenuItem>
          </Select>
        </FormControl>
        <TextField
          {...register("leverage")}
          error={!!(errors as any)?.leverage}
          helperText={(errors as any)?.leverage?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Leverage"
          name="leverage"
        />
        <TextField
          {...register("tag")}
          error={!!(errors as any)?.tag}
          helperText={(errors as any)?.tag?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Tag"
          name="tag"
        />
        <Controller
          control={control}
          name="show_explore"
          render={({ field: { onChange, value } }) => (
            <FormControl required error={!!(errors as any)?.show_explore}>
              <FormLabel>Show Explore</FormLabel>
              <RadioGroup
                row
                onChange={(_, value) => onChange(value === "true")}
                value={value}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes"
                  value={true}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No"
                  value={false}
                />
              </RadioGroup>
              <FormHelperText>
                {(errors as any)?.show_explore?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="is_actived"
          render={({ field: { onChange, value } }) => (
            <FormControl required error={!!(errors as any)?.is_actived}>
              <FormLabel>Active</FormLabel>
              <RadioGroup
                row
                onChange={(_, value) => onChange(value === "true")}
                value={value}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes"
                  value={true}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No"
                  value={false}
                />
              </RadioGroup>
              <FormHelperText>
                {(errors as any)?.is_actived?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="is_exclusive"
          render={({ field: { onChange, value } }) => (
            <FormControl required error={!!(errors as any)?.is_exclusive}>
              <FormLabel>Exclusive</FormLabel>
              <RadioGroup
                row
                onChange={(_, value) => onChange(value === "true")}
                value={value}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes"
                  value={true}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No"
                  value={false}
                />
              </RadioGroup>
              <FormHelperText>
                {(errors as any)?.is_exclusive?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
        <TextField
          {...register("trend_type")}
          error={!!(errors as any)?.trend_type}
          helperText={(errors as any)?.trend_type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Trend Type "
          name="trend_type"
        />
        <TextField
          {...register("risk_type")}
          error={!!(errors as any)?.risk_type}
          helperText={(errors as any)?.risk_type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Risk Type"
          name="risk_type"
        />
        {/* <TextField
          {...register("percent_win_rate")}
          error={!!(errors as any)?.percent_win_rate}
          helperText={(errors as any)?.percent_win_rate?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Percent Win Rate"
          name="percent_win_rate"
        />
        <TextField
          {...register("total_percent_return")}
          error={!!(errors as any)?.total_percent_return}
          helperText={(errors as any)?.total_percent_return?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Total Percent Return"
          name="total_percent_return"
        />
        <TextField
          {...register("percent_max_draw_down")}
          error={!!(errors as any)?.percent_max_draw_down}
          helperText={(errors as any)?.percent_max_draw_down?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Percent Max Draw Down"
          name="percent_max_draw_down"
        />
        <TextField
          {...register("recovery_period")}
          error={!!(errors as any)?.recovery_period}
          helperText={(errors as any)?.recovery_period?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Recovery period"
          name="recovery_period"
        />
        <TextField
          inputProps={{
            ...register("trade_count"),
          }}
          error={!!(errors as any)?.trade_count}
          helperText={(errors as any)?.trade_count?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Trade Count"
          name="trade_count"
        />
        <TextField
          {...register("backtest_start_at")}
          error={!!(errors as any)?.backtest_start_at}
          helperText={(errors as any)?.backtest_start_at?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="datetime-local"
          label="Backtest Start At"
          name="backtest_start_at"
        />
        <TextField
          {...register("backtest_end_at")}
          error={!!(errors as any)?.backtest_end_at}
          helperText={(errors as any)?.backtest_end_at?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="datetime-local"
          label="Backtest End At"
          name="backtest_end_at"
        /> */}
      </Box>
    </Create>
  );
};
